import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core'
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { ActiveCartFacade } from '@spartacus/cart/base/root'
import {
  CheckoutDeliveryAddressComponent,
  CheckoutStepService
} from '@spartacus/checkout/base/components'
import {
  CheckoutDeliveryAddressService,
  CheckoutDeliveryModesService
} from '@spartacus/checkout/base/core'
import {
  Address,
  Country,
  GlobalMessageService,
  TranslationService,
  UserAddressService
} from '@spartacus/core'
import { Card, LaunchDialogService } from '@spartacus/storefront'
import { BehaviorSubject, of } from 'rxjs'
import { take } from 'rxjs/operators'
import { EbUserAccountService } from 'src/app/features/user/services/eb-user-account.service'
import { EbContactFormTokenEnum } from '../../../support/contact-form/interfaces/eb-contact-form.interface'
import { EbAddress } from '../../interface/eb-checkout.interface'
import { EbCheckoutDeliveryService } from '../../services/facades/eb-checkout-delivery.service'

@Component({
    selector: 'eb-shipping-address',
    templateUrl: './eb-shipping-address.component.html',
    styleUrls: ['./eb-shipping-address.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class EbShippingAddressComponent extends CheckoutDeliveryAddressComponent {
  protected isLoaded$ = of(true)
  submitting$ = new BehaviorSubject(false)
  shippingAddressForm: UntypedFormGroup
  currentShippingAddressId: string
  currentAddress: EbAddress
  newAddressTitle: string
  country: Country
  personalAddressLabel: string
  constructor(
    protected userAddressService: UserAddressService,
    protected checkoutDeliveryService: CheckoutDeliveryAddressService,
    protected ebCheckoutDeliveryService: EbCheckoutDeliveryService,
    protected activatedRoute: ActivatedRoute,
    protected translation: TranslationService,
    protected activeCartService: ActiveCartFacade,
    protected checkoutStepService: CheckoutStepService,
    protected checkoutDeliveryModesService: CheckoutDeliveryModesService,
    protected globalMessageService: GlobalMessageService,
    protected modalService: LaunchDialogService,
    protected cd: ChangeDetectorRef,
    protected ebElement: ElementRef,
    protected vcr: ViewContainerRef,
    protected userAccountService: EbUserAccountService
  ) {
    super(
      userAddressService,
      checkoutDeliveryService,
      activatedRoute,
      translation,
      activeCartService,
      checkoutStepService,
      checkoutDeliveryModesService,
      globalMessageService
    )
    this.getNewAddressTitle()
    this.getCountry()
    this.getPersonalAddressLabel()
  }

  get ebContactFormTokenEnum() {
    return EbContactFormTokenEnum
  }

  getCardContent(
    address: EbAddress,
    selected: any,
    textDefaultShippingAddress: string,
    textShipToThisAddress: string,
    textSelected: string
  ): Card {
    return {
      label: address.ebCommerceAddress ?  this.personalAddressLabel : '',
      title: address.companyName ? address.companyName : '',
      text: [
        `${address.line1} ${address.line2}`,
        `${address.postalCode} ${address.town}`,
        address.country?.name || address.country?.isocode,
        address.name3 ? '' : undefined,
        address.name3
      ],
      actions: address.ebCommerceAddress || address.attentionOf ? [
        { name: 'edit', event: 'edit' },
        { name: 'delete', event: 'delete' }
      ] : [
          { name: 'edit', event: 'edit' },
      ],
      header: selected && selected.id === address.id ? textSelected : '',
      customClass: address.ebCommerceAddress ? '' : 'no-action-card'
    }
  }

  getNewAddressCardContent(): Card {
    return {
      img: 'EXPAND',
      actions: [{ name: 'new', event: 'new' }],
      title: this.newAddressTitle
    }
  }
  editAddress(card: EbAddress) {
    this.currentShippingAddressId = card.id
    this.createFormData(card)
    const dialog = this.modalService.openDialog('EDIT_ADDRESS', this.ebElement, this.vcr, {
      shippingAddressForm: this.shippingAddressForm,
      selectedAddress$: this.selectedAddress$,
      submitting$: this.submitting$,
      currentShippingAddressId: this.currentShippingAddressId
    })
    dialog?.pipe(take(1)).subscribe()
    this.cd.detectChanges()
  }
  deleteAddress(card: EbAddress) {
    const dialog = this.modalService.openDialog('DELETE_ADDRESS', this.ebElement, this.vcr, {
        address: card,
        submitting$: this.submitting$,
      }
    )
    dialog?.pipe(take(1)).subscribe()
    this.cd.detectChanges()
  }

  newAddress() {
    const addressTemplate = {
      companyName: '',
      line1: '',
      line2: '',
      town: '',
      postalCode: '',
      country: this.country,
      ebCommerceAddress: true
    } as EbAddress
    this.currentAddress = addressTemplate
    this.createFormData(addressTemplate)
    const dialog = this.modalService.openDialog('EDIT_ADDRESS', this.ebElement, this.vcr, {
      shippingAddressForm: this.shippingAddressForm,
      selectedAddress$: this.selectedAddress$,
      submitting$: this.submitting$,
      currentShippingAddressId: this.currentShippingAddressId
    })
    dialog?.pipe(take(1)).subscribe()
    this.cd.detectChanges()
  }

  createFormData(address: EbAddress) {
    this.currentAddress = address
    const isDisabled = !address.ebCommerceAddress
    this.shippingAddressForm = new UntypedFormGroup({
      companyName: new UntypedFormControl(
        { value: address.companyName, disabled: isDisabled },
        {
          updateOn: 'change'
        }
      ),
      street: new UntypedFormControl(
        { value: address.line1, disabled: isDisabled },
        {
          updateOn: 'change'
        }
      ),
      houseNumber: new UntypedFormControl(
        { value: address.line2, disabled: isDisabled },
        {
          updateOn: 'change'
        }
      ),
      city: new UntypedFormControl(
        { value: address.town, disabled: isDisabled },
        {
          updateOn: 'change'
        }
      ),
      zip: new UntypedFormControl(
        { value: address.postalCode, disabled: isDisabled },
        {
          updateOn: 'change'
        }
      ),
      country: new UntypedFormControl(
        { value: address.country.name, disabled: true },
        {
          updateOn: 'change'
        }
      ),
      additionalInformation: new UntypedFormControl(address.name3, {
        updateOn: 'change',
        validators: [Validators.maxLength(35)]
      }),
      id: new UntypedFormControl(address?.id),
      ebCommerceAddress: new UntypedFormControl(address.ebCommerceAddress)
    })
  }

  selectAddress(address: Address): void {
    this.ebCheckoutDeliveryService.setDeliveryAddress(address)
  }

  getNewAddressTitle() {
    this.translation.translate('checkoutProgress.newShippingAddress').subscribe(translation => {
      this.newAddressTitle = translation
    })
  }

  getCountry() {
    this.userAccountService.getEbActiveUserData().subscribe(user => {
      this.country = user.orgUnit.country
    })
  }

  getPersonalAddressLabel(){
    this.translationService.translate('shippingAddress.personalAddress').subscribe(translation => {
      this.personalAddressLabel = translation
    })
  }
}
